import React from 'react';
import { AlertTriangle, PackageX, RefreshCw, ShieldCheck, Truck } from 'lucide-react';
import { cn } from '@/utils/cn';

/** One open reason this customer is on a call list, scoped to the order it concerns. */
export interface ActiveIssue {
    issue_uuid: string;
    issue_type: 'edd' | 'ndr' | 'rto' | 'reorder' | 'cod_confirm';
    label: string;
    order_uuid: string | null;
    order_name: string | null;
    product_title: string | null;
    opened_at: string | null;
    due_at: string | null;
    days_overdue: number;
    current_status: string | null;
    current_status_code: number | null;
    ndr_reason: string | null;
    ndr_attempt_count: number;
    // EDD / NDR / RTO are dispositioned on the work item itself ('work_item'); reorder
    // on the customer ('customer'). disposition_bucket is the tree to load.
    disposition_scope?: 'work_item' | 'customer';
    disposition_bucket?: string;
    disposition_l1?: number | null;
    disposition_l2?: number | null;
    disposition_l3?: number | null;
    disposition_l1_value?: string | null;
    disposition_l2_value?: string | null;
    disposition_l3_value?: string | null;
    work_status?: string | null;
    last_contact_at?: string | null;
}

const ISSUE_STYLE: Record<ActiveIssue['issue_type'], { icon: React.ElementType; tone: string; active: string }> = {
    ndr:     { icon: AlertTriangle, tone: 'border-red-200 bg-red-50 text-red-700',        active: 'border-red-500 bg-red-100 ring-1 ring-red-400' },
    rto:     { icon: PackageX,      tone: 'border-orange-200 bg-orange-50 text-orange-700', active: 'border-orange-500 bg-orange-100 ring-1 ring-orange-400' },
    edd:     { icon: Truck,         tone: 'border-blue-200 bg-blue-50 text-blue-700',     active: 'border-blue-500 bg-blue-100 ring-1 ring-blue-400' },
    reorder: { icon: RefreshCw,     tone: 'border-green-200 bg-green-50 text-green-700',  active: 'border-green-500 bg-green-100 ring-1 ring-green-400' },
    cod_confirm: { icon: ShieldCheck, tone: 'border-purple-200 bg-purple-50 text-purple-700', active: 'border-purple-500 bg-purple-100 ring-1 ring-purple-400' },
};

interface Props {
    issues: ActiveIssue[];
    selectedIssueUuid: string | null;
    // null = the customer's own disposition (Sales / Retention), not an order's.
    onSelect: (issue: ActiveIssue | null) => void;
}

/**
 * The customer's open issues, one chip each.
 *
 * A customer can genuinely need two different calls at once — a failed delivery on one
 * order while another is coming back — which the single lead_bucket column could never
 * represent, so one of the two was silently dropped. Showing them together is half the
 * fix; the other half is that every action below this bar states which order it applies
 * to, so an agent working a two-issue customer can't disposition the wrong one.
 *
 * Renders nothing for the ordinary single-issue customer — no bar, no noise.
 */
const ActiveIssuesBar: React.FC<Props> = ({ issues, selectedIssueUuid, onSelect }) => {
    // Shown whenever there is any open order work, so an agent always sees whether a
    // save lands on the customer or on an order.
    if (!issues || issues.length < 1) return null;

    return (
        <div className="px-4 py-2.5 border-b border-amber-200 bg-amber-50/60">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-semibold text-amber-900 uppercase tracking-wide shrink-0">
                    Save outcome on:
                </span>
                <button
                    type="button"
                    onClick={() => onSelect(null)}
                    aria-pressed={selectedIssueUuid === null}
                    title="The customer's own outcome (Sales / Retention)"
                    className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors',
                        selectedIssueUuid === null
                            ? 'border-gray-600 bg-gray-100 ring-1 ring-gray-500 text-gray-900'
                            : 'border-gray-300 bg-white text-gray-700',
                        'hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-amber-400'
                    )}
                >
                    Customer
                </button>
                {issues.map((issue) => {
                    const style = ISSUE_STYLE[issue.issue_type] ?? ISSUE_STYLE.edd;
                    const Icon = style.icon;
                    const isSelected = issue.issue_uuid === selectedIssueUuid;
                    return (
                        <button
                            key={issue.issue_uuid}
                            type="button"
                            onClick={() => onSelect(issue)}
                            aria-pressed={isSelected}
                            title={[
                                issue.label,
                                issue.order_name ? `Order ${issue.order_name}` : null,
                                issue.product_title,
                                issue.current_status,
                                issue.ndr_attempt_count > 0 ? `${issue.ndr_attempt_count} failed attempt(s)` : null,
                                issue.days_overdue > 0 ? `${issue.days_overdue} days overdue` : null,
                                issue.disposition_scope === 'work_item'
                                    ? `Outcome: ${[issue.disposition_l1_value, issue.disposition_l2_value, issue.disposition_l3_value].filter(Boolean).join(' / ') || 'New'}`
                                    : null,
                            ].filter(Boolean).join(' · ')}
                            className={cn(
                                'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors',
                                isSelected ? style.active : style.tone,
                                'hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-amber-400'
                            )}
                        >
                            <Icon className="w-3 h-3 shrink-0" />
                            <span>{issue.label}</span>
                            {issue.order_name && (
                                <span className="opacity-70">{issue.order_name}</span>
                            )}
                            {issue.days_overdue > 0 && (
                                <span className="font-semibold">· {issue.days_overdue}d late</span>
                            )}
                            {/* This order's own outcome — each work item keeps its own */}
                            {issue.disposition_scope === 'work_item' && (
                                <span className="opacity-80">
                                    · {issue.disposition_l1_value || 'New'}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default ActiveIssuesBar;
